import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Avatar,
} from '@mui/material';
import { LockOutlined as LockIcon } from '@mui/icons-material';
import axios from 'axios';

const Login = ({ onLoginSuccess }) => {
  const [firstName, setFirstName] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!firstName.trim() || !pin.trim()) {
      setError('Please enter both first name and PIN');
      setLoading(false);
      return;
    }

    if (!/^\d{4}$/.test(pin)) {
      setError('PIN must be exactly 4 digits');
      setLoading(false);
      return;
    }

    try {
      const endpoint = isRegistering ? '/api/auth/register' : '/api/auth/login';
      const response = await axios.post(endpoint, {
        firstName: firstName.trim(),
        pin
      });

      if (response.data.requiresApproval) {
        setError(response.data.message);
        setIsRegistering(false);
        setPin('');
      } else if (response.data.success && response.data.sessionToken && response.data.user) {
        // Store session token
        localStorage.setItem('sessionToken', response.data.sessionToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.sessionToken}`;
        
        onLoginSuccess(response.data.user);
      }
    } catch (error) {
      console.error('Auth error:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(value);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <img 
                src="https://www.xs-stock.co.uk/cdn/shop/files/XS_NEW_LOGO_2ac57727-dd78-4de8-bd11-0c0bb84a6858.png?v=1738612353&width=400" 
                alt="XS Stock"
                style={{ height: '48px', marginBottom: '16px' }}
              />
              <Avatar sx={{ bgcolor: 'primary.main', mb: 2 }}>
                <LockIcon />
              </Avatar>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
                {isRegistering ? 'Register' : 'Sign In'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                XS Returns Management System
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoFocus
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="4-Digit PIN"
                type="password"
                value={pin}
                onChange={handlePinChange}
                inputProps={{ 
                  maxLength: 4,
                  pattern: "[0-9]{4}",
                  style: { letterSpacing: '8px', textAlign: 'center' }
                }}
                placeholder="0000"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? 'Please wait...' : (isRegistering ? 'Register' : 'Sign In')}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setIsRegistering(!isRegistering);
                  setError('');
                  setFirstName('');
                  setPin('');
                }}
                sx={{ mt: 1 }}
              >
                {isRegistering ? 'Already have access? Sign In' : 'Need access? Register'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;
